// frontend/src/ConfigModal.js
import React, { useState, useEffect, useCallback } from 'react';
import Modal from 'react-modal';
import './App.css'; // Reuse styles

const customModalStyles = { // Reusing style definition
    content: {
      top: '50%', left: '50%', right: 'auto', bottom: 'auto',
      marginRight: '-50%', transform: 'translate(-50%, -50%)',
      maxWidth: '600px', // Slightly wider to accommodate new section
      maxHeight: '85vh', width: '90%', overflow: 'auto',
      padding: '0', // Remove padding for tab structure
      border: '1px solid #ccc', borderRadius: '8px',
      boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)', backgroundColor: '#fff',
      display: 'flex', flexDirection: 'column', // Added for layout
    },
    overlay: { backgroundColor: 'rgba(0, 0, 0, 0.6)', zIndex: 1000 },
};

// Ensure Modal root is set (usually in App.js or index.js)
// Modal.setAppElement('#root'); // Example

function ConfigModal({
    isOpen,
    onRequestClose,
    // Explorer Config Props
    currentConfig,
    onSave, // Renamed from handleSaveExplorerConfig (receives handleSaveConfig from App.js via AppExplorer)
    // Data Source Props (Passed from AppExplorer)
    currentFilterType,
    currentSelectedDirectory,
    currentSelectedCollection,
    directories = [], // Available directories
    onSaveDataSource, // Function to save data source changes in AppExplorer
    isAuthenticated,
    handleLogout, // Needed for auth errors during collection fetch
    setError, // To report fetch errors
}) {

    // === State ===
    // --- Explorer Config State ---
    const [localMaxLoops, setLocalMaxLoops] = useState(currentConfig.maxLoops);
    const [localAnalyzeResponse, setLocalAnalyzeResponse] = useState(currentConfig.analyzeResponse);
    const [localVerifyCheckin, setLocalVerifyCheckin] = useState(currentConfig.verifyCheckin);
    const [localUseRAG, setLocalUseRAG] = useState(currentConfig.useRAG);

    // --- Data Source State (Local to Modal) ---
    const [localFilterType, setLocalFilterType] = useState(currentFilterType);
    const [localSelectedDirectory, setLocalSelectedDirectory] = useState(currentSelectedDirectory);
    const [localSelectedCollection, setLocalSelectedCollection] = useState(currentSelectedCollection);
    const [collectionNames, setCollectionNames] = useState([]);
    const [isFetchingCollections, setIsFetchingCollections] = useState(false);
    const [collectionFetchError, setCollectionFetchError] = useState(null);

    // --- UI State ---
    const [activeTab, setActiveTab] = useState('Data Source'); // Default tab

    // === Effects ===
    // Update local state if props change (e.g., loaded from log)
    useEffect(() => {
        setLocalMaxLoops(currentConfig.maxLoops);
        setLocalAnalyzeResponse(currentConfig.analyzeResponse);
        setLocalVerifyCheckin(currentConfig.verifyCheckin);
        setLocalUseRAG(currentConfig.useRAG);
        setLocalFilterType(currentFilterType);
        setLocalSelectedDirectory(currentSelectedDirectory);
        setLocalSelectedCollection(currentSelectedCollection);
        setCollectionFetchError(null); // Reset collection error when modal opens or filter type changes
    }, [
        currentConfig,
        currentFilterType,
        currentSelectedDirectory,
        currentSelectedCollection,
        isOpen // Re-sync when modal opens
    ]);

    // Fetch collections when filter type changes to 'collection' *locally*
    const fetchCollectionNames = useCallback(async () => {
        if (!isAuthenticated || localFilterType !== 'collection') return;
        if (collectionNames.length > 0 && !isFetchingCollections) return; // Avoid refetch

        console.log("ConfigModal: Fetching collection names...");
        setIsFetchingCollections(true);
        setCollectionFetchError(null);
        try {
            const response = await fetch('/api/files/get_collection_names', { credentials: 'include' });
            if (response.ok) {
                const data = await response.json();
                setCollectionNames(data || []);
                console.log("ConfigModal: Collection names fetched:", data);
            } else if (response.status === 401) {
                console.warn("ConfigModal: Session expired fetching collections. Logging out.");
                if (handleLogout) handleLogout(false);
                setCollectionFetchError("Session expired. Please log in again.");
            } else {
                const errorData = await response.text();
                console.error(`ConfigModal: Failed to fetch collections: ${response.status} - ${errorData}`);
                setCollectionFetchError(`Failed to load collections (Status: ${response.status}).`);
                setCollectionNames([]);
            }
        } catch (err) {
            console.error("ConfigModal: Network error fetching collections:", err);
            setCollectionFetchError("Network error fetching collections.");
            setCollectionNames([]);
        } finally {
            setIsFetchingCollections(false);
        }
    }, [isAuthenticated, localFilterType, handleLogout, collectionNames.length, isFetchingCollections]);

    useEffect(() => {
        if (isOpen && localFilterType === 'collection') {
             fetchCollectionNames();
        }
    }, [isOpen, localFilterType, fetchCollectionNames]);

    // === Handlers ===
    const handleSaveClick = () => {
        // Call the function passed via the 'onSave' prop to update App.js state
        onSave({
            maxLoops: parseInt(localMaxLoops, 10) || 5,
            analyzeResponse: localAnalyzeResponse,
            verifyCheckin: parseInt(localVerifyCheckin, 10) || 1,
            useRAG: localUseRAG,
        });
        // Save Data Source Config (updates AppExplorer state)
        onSaveDataSource({
            filterType: localFilterType,
            selectedDirectory: localFilterType === 'directory' ? localSelectedDirectory : '',
            selectedCollection: localFilterType === 'collection' ? localSelectedCollection : '',
        });
        onRequestClose(); // Close modal after saving
    };

    const handleFilterTypeChange = (e) => {
        const newType = e.target.value;
        setLocalFilterType(newType);
        setLocalSelectedDirectory(''); // Clear selections when changing type
        setLocalSelectedCollection('');
        if (newType === 'collection') {
             fetchCollectionNames(); // Trigger fetch immediately
        }
    };

    // === Render Logic ===
    return (
        <Modal
            isOpen={isOpen}
            onRequestClose={onRequestClose}
            style={customModalStyles}
            contentLabel="Configuration Settings"
            ariaHideApp={false} // Avoids console warning, set appropriately
        >
            {/* --- Modal Header --- */}
            <div style={{ padding: '15px 20px', borderBottom: '1px solid #eee', fontWeight: 'bold', fontSize: '1.1em', flexShrink: 0 }}>
                Configuration Settings
            </div>

            {/* --- Modal Body (Tabs + Content) --- */}
            <div style={{ padding: '20px', flexGrow: 1, overflowY: 'auto' }}>
                 {/* --- Tab Buttons --- */}
                 <div className="config-tabs" style={{ marginBottom: '20px', borderBottom: '1px solid #ccc', paddingBottom: '0' }}>
                    {['Data Source', 'Explorer Config'].map((tab) => (
                        <button
                            key={tab}
                            className={`config-tab ${activeTab === tab ? 'active' : ''}`}
                            onClick={() => setActiveTab(tab)}
                            style={{ marginRight: '5px', marginBottom: '-1px' }}
                        >
                            {tab}
                        </button>
                    ))}
                 </div>

                 {/* --- Tab Content --- */}
                 <div className="config-page-area" style={{ padding: '0', border: 'none', boxShadow: 'none' }}>

                    {/* --- Data Source Tab --- */}
                    {activeTab === 'Data Source' && (
                        <div className="data-source-config">
                            <h4>Select Data Source</h4>
                            <div className="form-section" style={{ marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '15px' }}>
                                <label style={{ fontWeight: 'normal', marginBottom: '0' }}>Filter By:</label>
                                <div>
                                    <input type="radio" id="modalFilterTypeDirectory" name="modalFilterType" value="directory" checked={localFilterType === 'directory'} onChange={handleFilterTypeChange} />
                                    <label htmlFor="modalFilterTypeDirectory" style={{ marginRight: '10px', fontWeight: 'normal', cursor: 'pointer' }}>Directory</label>
                                    <input type="radio" id="modalFilterTypeCollection" name="modalFilterType" value="collection" checked={localFilterType === 'collection'} onChange={handleFilterTypeChange} />
                                    <label htmlFor="modalFilterTypeCollection" style={{ fontWeight: 'normal', cursor: 'pointer' }}>Collection</label>
                                </div>
                            </div>
                            {localFilterType === 'directory' && (
                                <div className="form-section">
                                    <label htmlFor="modalDirectorySelect">Directory:</label>
                                    <select id="modalDirectorySelect" value={localSelectedDirectory} onChange={(e) => setLocalSelectedDirectory(e.target.value)} required={localFilterType === 'directory'} className="control-panel select" style={{ width: '100%' }}>
                                        <option value="" disabled>-- Select Directory --</option>
                                        {directories.map(dir => <option key={dir} value={dir}>{dir}</option>)}
                                    </select>
                                </div>
                            )}
                            {localFilterType === 'collection' && (
                                <div className="form-section">
                                    <label htmlFor="modalCollectionSelect">Collection:</label>
                                    <select id="modalCollectionSelect" value={localSelectedCollection} onChange={(e) => setLocalSelectedCollection(e.target.value)} required={localFilterType === 'collection'} disabled={isFetchingCollections} className="control-panel select" style={{ width: '100%' }}>
                                        <option value="" disabled>{isFetchingCollections ? 'Loading...' : '-- Select Collection --'}</option>
                                        {collectionNames.map(name => <option key={name} value={name}>{name}</option>)}
                                    </select>
                                    {collectionFetchError && <div className="error" style={{ marginTop: '5px', fontSize: '0.9em' }}>{collectionFetchError}</div>}
                                    {!isFetchingCollections && collectionNames.length === 0 && !collectionFetchError && ( <small style={{ color: 'orange', display: 'block', marginTop: '4px' }}>No collections found or available.</small> )}
                                </div>
                            )}
                            <small style={{ color: '#6c757d', display: 'block', marginTop: '15px' }}>Select whether to explore data from a specific Directory or a curated Collection of files.</small>
                        </div>
                    )}

                    {/* --- Explorer Config Tab --- */}
                    {activeTab === 'Explorer Config' && (
                         <div className="explorer-settings-config">
                            <h4>Explorer Settings</h4>
                            <div className="form-section">
                                <label htmlFor="maxLoops">Max Loops:</label>
                                <input id="maxLoops" type="number" value={localMaxLoops} onChange={(e) => setLocalMaxLoops(e.target.value)} min="1" step="1" className="control-panel input"/>
                                <small>Maximum exploration steps before forcing completion.</small>
                            </div>
                            <div className="form-section">
                                <label htmlFor="verifyCheckin">Verify Frequency:</label>
                                <input id="verifyCheckin" type="number" value={localVerifyCheckin} onChange={(e) => setLocalVerifyCheckin(Math.max(1, parseInt(e.target.value) || 1))} min="1" step="1" className="control-panel input"/>
                                <small>Check completion with Manager LLM every N loops (1 = always).</small>
                            </div>
                            <div className="form-section checkbox-group">
                                <label className="checkbox-label"><input id="analyzeResponse" type="checkbox" checked={localAnalyzeResponse} onChange={(e) => setLocalAnalyzeResponse(e.target.checked)} /> Analyze LLM Reasoning </label>
                                <small>Run an extra LLM call per step to explain choices (adds cost/time).</small>
                            </div>
                            <div className="form-section checkbox-group">
                                <label className="checkbox-label"><input id="useRAG" type="checkbox" checked={localUseRAG} onChange={(e) => setLocalUseRAG(e.target.checked)} /> Use RAG Context </label>
                                <small>Fetch and use context from similar documents via vector search.</small>
                            </div>
                        </div>
                    )}
                 </div> {/* End Tab Content */}
            </div> {/* End Modal Body */}

            {/* --- Modal Footer --- */}
            <div style={{ padding: '10px 20px', borderTop: '1px solid #eee', textAlign: 'right', flexShrink: 0 }}>
                 <button onClick={handleSaveClick} className="modal-save-button" style={{ marginRight: '10px' }}>Save Settings</button>
                 <button onClick={onRequestClose} className="modal-cancel-button">Cancel</button>
            </div>
        </Modal>
    );
}

export default ConfigModal;